import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Building2, ArrowLeft, ArrowRight, Loader2, Save, FolderOpen, Trash2, AlertCircle, ChevronDown, ChevronUp, LogOut, User, Copy, Shield, Coins, Download, Upload, Share2, X, Clock } from "lucide-react";
import { toast } from "sonner";
import ResultsView from "@/components/ResultsView";
import FSICalculator from "@/components/FSICalculator";
import SocietyFinancials from "@/components/SocietyFinancials";
import FSISanctionCosts from "@/components/FSISanctionCosts";
import DACharges from "@/components/DACharges";
import OtherCosts from "@/components/OtherCosts";
import RevenueAnalysis from "@/components/RevenueAnalysis";
import PLSheet from "@/components/PLSheet";
import SensitivityAnalysis from "@/components/SensitivityAnalysis";
import QuickInput from "@/components/QuickInput";
import Dashboard from "@/components/Dashboard";
import Analysis from "@/components/Analysis";
import CashFlowAnalysis from "@/components/CashFlowAnalysis";
import ExpenseCashFlow from "@/components/ExpenseCashFlow";
import { calculateOurOfferMetrics, formatCrores as formatCroresShared, computeFSIBreakdownFromFormData } from "@/utils/calculations";

// Import refactored constants, hooks, and tab components
import { defaultFormData, emptyCompetitor, createCompetitor, defaultConstructionCosts } from "./CalculatorPage/constants";
import { useCredits, useFSIConfig, useProjectSharing, useExportImport } from "./CalculatorPage/hooks";
import ComparisonTab from "./CalculatorPage/ComparisonTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults for authentication
axios.defaults.withCredentials = true;

export default function CalculatorPage({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [fsiBreakdown, setFSIBreakdown] = useState(null);
  const [fsiCosts, setFSICosts] = useState(null);
  
  const [activeTab, setActiveTab] = useState("quickinput");
  const [quickInputHasErrors, setQuickInputHasErrors] = useState(true); // Start with errors (empty form)
  const [daChargesCosts, setDAChargesCosts] = useState(null);
  const [societyCosts, setSocietyCosts] = useState(null);
  const [otherCostsData, setOtherCostsData] = useState(null);
  const [showSocietyBreakdown, setShowSocietyBreakdown] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSaveTime, setLastAutoSaveTime] = useState(null);
  
  // Project save/load state
  const [savedProjects, setSavedProjects] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [saveProjectName, setSaveProjectName] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [lastOpenedInfo, setLastOpenedInfo] = useState(null);
  
  // Use extracted hooks for credits and FSI config
  const {
    userCredits,
    setUserCredits,
    requestingCredits,
    creditRequestPending,
    fetchCredits,
    checkCreditRequestStatus,
    handleRequestCredits
  } = useCredits(user?.project_credits ?? 0);
  
  const { constructionCosts, appConfig, fetchFSIConfig } = useFSIConfig();

  // Fetch credits and FSI config on mount
  useEffect(() => {
    fetchCredits();
    checkCreditRequestStatus();
    fetchFSIConfig();
  }, [fetchCredits, checkCreditRequestStatus, fetchFSIConfig]);

  // Logout handler
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('session_token');
      toast.success("Logged out successfully");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      localStorage.removeItem('session_token');
      navigate("/login");
    }
  };

  // Use imported defaultFormData with an initial competitor
  const [formData, setFormData] = useState(() => ({
    ...defaultFormData,
    competitors: [createCompetitor()]
  }));

  // Auto-save refs for debouncing
  const lastSaveTimeRef = useRef(0);
  const saveTimeoutRef = useRef(null);
  const isDirtyRef = useRef(false);
  const lastSavedDataRef = useRef(null);
  const formDataRef = useRef(formData);
  const currentProjectIdRef = useRef(currentProjectId);
  // Ref for plData so autoSaveProject (memoized) always reads the latest computed metrics.
  // Without this, the captured `plData` would be from the first render (stale snapshots in DB).
  const plDataRef = useRef(null);

  // Keep refs in sync
  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { currentProjectIdRef.current = currentProjectId; }, [currentProjectId]);

  // Auto-save function (debounced, silent save without dialog)
  // Project name = Society Name
  const autoSaveProject = useCallback(async (force = false) => {
    const fd = formDataRef.current;
    const projId = currentProjectIdRef.current;
    const projectName = fd.societyName;
    if (!projectName || projectName.trim() === "") {
      return;
    }
    
    // Debounce: skip if last save was less than 60 seconds ago (unless forced)
    const now = Date.now();
    if (!force && (now - lastSaveTimeRef.current) < 60000) {
      return;
    }

    // Skip if data hasn't changed since last save
    const dataSnapshot = JSON.stringify(fd);
    if (!force && lastSavedDataRef.current === dataSnapshot) {
      return;
    }
    
    setIsAutoSaving(true);
    try {
      // Read latest plData via ref (autoSaveProject is memoized, so direct closure would be stale)
      const pd = plDataRef.current;
      // Compute metrics snapshot to save alongside form_data for cross-project comparison
      const metricsSnapshot = pd ? {
        totalFSIArea: pd.totalFSIArea,
        finalSaleAreaSqft: pd.finalSaleAreaSqft,
        developerSaleAreaSqft: pd.developerSaleAreaSqft,
        saleRatePerSqft: pd.saleRatePerSqft,
        totalSaleRevenue: pd.totalSaleRevenue,
        totalRevenue: pd.totalRevenue,
        fsiTotalCosts: pd.fsiTotalCosts,
        totalDACharges: pd.totalDACharges,
        totalSocietyExpenses: pd.totalSocietyExpenses,
        totalConstructionCost: pd.totalConstructionCost,
        constructionCostPerSqft: pd.constructionCostPerSqft,
        totalOtherCosts: pd.totalOtherCosts,
        totalBankInterest: pd.totalBankInterest,
        pmcCost: pd.pmcCost,
        totalExpenses: pd.totalExpenses,
        grossProfit: pd.grossProfit,
        profitMarginPercent: pd.profitMarginPercent,
        // Derive per-sft values (not present in plData itself but used by Comparison tab)
        costPerSqft: pd.finalSaleAreaSqft > 0 ? pd.totalExpenses / pd.finalSaleAreaSqft : 0,
        profitPerSqft: pd.finalSaleAreaSqft > 0 ? pd.grossProfit / pd.finalSaleAreaSqft : 0,
        societySharePercent: pd.societySharePercent,
        developerSharePercent: pd.developerSharePercent,
        durationYears: pd.durationYears,
        totalMarketingCost: pd.totalMarketingCost,
        bankGuaranteeCost: pd.bankGuaranteeCost,
        // Capital required up front (project's initial cash outlay)
        initialInvestment: pd.initialInvestment,
        isGreenfield: pd.isGreenfield,
      } : null;

      if (projId) {
        await axios.put(`${API}/saved-projects/${projId}`, {
          name: projectName,
          form_data: { ...fd, projectName: projectName },
          computed_metrics: metricsSnapshot
        });
      } else {
        const response = await axios.post(`${API}/saved-projects`, {
          name: projectName,
          form_data: { ...fd, projectName: projectName },
          computed_metrics: metricsSnapshot
        });
        setCurrentProjectId(response.data.id);
      }
      lastSaveTimeRef.current = Date.now();
      lastSavedDataRef.current = dataSnapshot;
      isDirtyRef.current = false;
      setLastAutoSaveTime(new Date());
    } catch (error) {
      console.error("Auto-save failed:", error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [API, setCurrentProjectId]);

  // Periodic auto-save every 2 minutes
  useEffect(() => {
    const intervalId = setInterval(() => {
      autoSaveProject();
    }, 120000);

    return () => clearInterval(intervalId);
  }, [autoSaveProject]);

  // Handle tab change with debounced auto-save
  const handleTabChange = (newTab) => {
    // Block navigation away from quickinput if there are validation errors
    if (activeTab === "quickinput" && newTab !== "quickinput" && quickInputHasErrors) {
      toast.error("Please fill all required fields before proceeding");
      return;
    }
    
    // Schedule a debounced save (will be skipped if one happened recently or data unchanged)
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => autoSaveProject(), 2000);
    setActiveTab(newTab);
  };

  // Quick Jump: force-save the latest changes, then navigate directly to a chosen tab
  const handleQuickJump = useCallback(async (targetTab) => {
    if (quickInputHasErrors) {
      toast.error("Please fill all required fields before proceeding");
      return;
    }
    // Force a save so computed_metrics snapshot stays in sync (only if project has a name)
    try {
      await autoSaveProject(true);
    } catch (err) {
      // Save failure shouldn't block navigation
      console.warn("Quick Jump save warning:", err);
    }
    setActiveTab(targetTab);
    toast.success(`Jumped to ${targetTab === "plsheet" ? "P&L Sheet" : targetTab}`);
  }, [quickInputHasErrors, autoSaveProject]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-fill society name when project name changes
    if (name === "projectName") {
      setFormData({
        ...formData,
        projectName: value,
        societyName: value
      });
      
      // Trigger first auto-save when project name is entered for a new project
      // This creates the project immediately so user doesn't lose work
      if (value.trim() && !currentProjectId && !lastSavedDataRef.current) {
        // Debounce the first save to wait until user stops typing
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
          autoSaveProject(true); // Force save for new project
        }, 3000); // 3 second delay after typing stops
      }
    } else if (name === "freeAreaPercentFlats") {
      // Sync additionalFreeAreaPercent for backward compatibility
      setFormData({
        ...formData,
        freeAreaPercentFlats: value,
        additionalFreeAreaPercent: value
      });
    } else if (name === "additionalFreeAreaPercent") {
      // Sync freeAreaPercentFlats from property tab
      setFormData({
        ...formData,
        additionalFreeAreaPercent: value,
        freeAreaPercentFlats: value
      });
    } else if (name === "directAreaOfferedSqft") {
      // Direct area mode: back-calculate free % and sync it
      const directSqft = parseFloat(value) || 0;
      const existingSqft = (parseFloat(formData.areaFlats) || 0) + (parseFloat(formData.areaOffices) || 0) + (parseFloat(formData.areaShops) || 0);
      const backCalcPct = existingSqft > 0 ? ((directSqft - existingSqft) / existingSqft * 100) : 0;
      const pctStr = backCalcPct > 0 ? backCalcPct.toFixed(1) : "0";
      setFormData({
        ...formData,
        directAreaOfferedSqft: value,
        // Auto-sync the back-calculated % so all downstream calcs use it
        freeAreaPercentFlats: pctStr,
        additionalFreeAreaPercent: pctStr
      });
    } else if (name === "areaOfferMode") {
      // When switching back to percentage mode, clear the direct sync
      setFormData({
        ...formData,
        areaOfferMode: value
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // ============ COMPETITOR MANAGEMENT FUNCTIONS ============
  
  const addCompetitor = () => {
    if (formData.competitors.length < 5) {
      setFormData({
        ...formData,
        competitors: [...formData.competitors, createCompetitor()]
      });
    }
  };
  
  const removeCompetitor = (index) => {
    if (formData.competitors.length > 1) {
      const newCompetitors = formData.competitors.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        competitors: newCompetitors,
        awardedToCompetitorIndex: formData.awardedToCompetitorIndex >= newCompetitors.length 
          ? 0 
          : formData.awardedToCompetitorIndex
      });
    }
  };
  
  const updateCompetitor = (index, field, value) => {
    const newCompetitors = [...formData.competitors];
    newCompetitors[index] = { ...newCompetitors[index], [field]: value };
    setFormData({
      ...formData,
      competitors: newCompetitors
    });
  };

  // ============ PROJECT SAVE/LOAD FUNCTIONS ============
  
  // Fetch saved projects list
  const fetchSavedProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await axios.get(`${API}/saved-projects`);
      setSavedProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  // Save current project
  const handleSaveProject = async () => {
    const projectName = saveProjectName || formData.projectName || "Untitled Project";
    
    try {
      if (currentProjectId) {
        // Update existing project
        const response = await axios.put(`${API}/saved-projects/${currentProjectId}`, {
          name: projectName,
          form_data: formData,
          computed_metrics: plData ? {
            totalFSIArea: plData.totalFSIArea, finalSaleAreaSqft: plData.finalSaleAreaSqft, developerSaleAreaSqft: plData.developerSaleAreaSqft, saleRatePerSqft: plData.saleRatePerSqft, totalSaleRevenue: plData.totalSaleRevenue, totalRevenue: plData.totalRevenue, fsiTotalCosts: plData.fsiTotalCosts, totalDACharges: plData.totalDACharges, totalSocietyExpenses: plData.totalSocietyExpenses, totalConstructionCost: plData.totalConstructionCost, constructionCostPerSqft: plData.constructionCostPerSqft, totalOtherCosts: plData.totalOtherCosts, totalBankInterest: plData.totalBankInterest, pmcCost: plData.pmcCost, totalExpenses: plData.totalExpenses, grossProfit: plData.grossProfit, profitMarginPercent: plData.profitMarginPercent, costPerSqft: plData.costPerSqft, profitPerSqft: plData.profitPerSqft, societySharePercent: plData.societySharePercent, developerSharePercent: plData.developerSharePercent, durationYears: plData.durationYears, isGreenfield: plData.isGreenfield,
          } : null
        });
        // Check if credit was used for rename
        if (response.data.credit_used) {
          fetchCredits(); // Refresh credits
          toast.success("Project renamed (1 credit used)");
        } else {
          toast.success("Project updated successfully");
        }
      } else {
        // Create new project
        const response = await axios.post(`${API}/saved-projects`, {
          name: projectName,
          form_data: formData,
          computed_metrics: plData ? {
            totalFSIArea: plData.totalFSIArea, finalSaleAreaSqft: plData.finalSaleAreaSqft, developerSaleAreaSqft: plData.developerSaleAreaSqft, saleRatePerSqft: plData.saleRatePerSqft, totalSaleRevenue: plData.totalSaleRevenue, totalRevenue: plData.totalRevenue, fsiTotalCosts: plData.fsiTotalCosts, totalDACharges: plData.totalDACharges, totalSocietyExpenses: plData.totalSocietyExpenses, totalConstructionCost: plData.totalConstructionCost, constructionCostPerSqft: plData.constructionCostPerSqft, totalOtherCosts: plData.totalOtherCosts, totalBankInterest: plData.totalBankInterest, pmcCost: plData.pmcCost, totalExpenses: plData.totalExpenses, grossProfit: plData.grossProfit, profitMarginPercent: plData.profitMarginPercent, costPerSqft: plData.costPerSqft, profitPerSqft: plData.profitPerSqft, societySharePercent: plData.societySharePercent, developerSharePercent: plData.developerSharePercent, durationYears: plData.durationYears, isGreenfield: plData.isGreenfield,
          } : null
        });
        setCurrentProjectId(response.data.id);
        fetchCredits(); // Refresh credits after new project
        toast.success("Project saved (1 credit used)");
      }
      setSaveDialogOpen(false);
      setSaveProjectName("");
    } catch (error) {
      console.error("Error saving project:", error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || "Insufficient credits. Request more from admin.");
      } else {
        toast.error("Failed to save project");
      }
    }
  };

  // Load a saved project
  const handleLoadProject = async (projectId) => {
    try {
      const response = await axios.get(`${API}/saved-projects/${projectId}`);
      
      // Merge loaded data with default values to handle missing fields
      const loadedData = response.data.form_data;
      
      // Ensure competitors array exists (for backward compatibility)
      if (!loadedData.competitors || !Array.isArray(loadedData.competitors)) {
        loadedData.competitors = [{ ...emptyCompetitor }];
        
        // Migrate old single competitor data if it exists
        if (loadedData.competitorName || loadedData.competitorAdditionalAreaPercent) {
          loadedData.competitors[0] = {
            name: loadedData.competitorName || "",
            additionalAreaPercent: loadedData.competitorAdditionalAreaPercent || "",
            areaSharePercent: loadedData.competitorAreaSharingPercent || "",
            rentPerSqftFlat: loadedData.competitorRentPerSqftFlat || "",
            rentPerSqftShop: loadedData.competitorRentPerSqftShop || "",
            bettermentCharges: loadedData.competitorBettermentCharges || "",
            bettermentChargesType: "per_member", // Default for migrated data
            corpusCharges: loadedData.competitorCorpusCharges || "",
            shiftingCharges: loadedData.competitorShiftingCharges || "",
            projectDuration: loadedData.competitorProjectDuration || "",
          };
        }
      }
      
      // Ensure each competitor has bettermentChargesType (for backward compatibility)
      loadedData.competitors = loadedData.competitors.map(comp => ({
        ...comp,
        bettermentChargesType: comp.bettermentChargesType || "per_member"
      }));
      
      // Ensure awardedToCompetitorIndex exists
      if (loadedData.awardedToCompetitorIndex === undefined) {
        loadedData.awardedToCompetitorIndex = 0;
      }
      
      setFormData(loadedData);
      setCurrentProjectId(projectId);
      setLoadDialogOpen(false);
      setIsProjectOwner(response.data.is_owner !== false);
      setLastOpenedInfo(
        response.data.last_accessed 
          ? { date: response.data.last_accessed, by: response.data.last_accessed_by || null }
          : null
      );
      fetchSharedUsers(projectId);
      toast.success(`Loaded: ${response.data.name}`);

      // Refresh the stored computed_metrics snapshot so the Comparison tab always
      // reflects what the live software currently shows (fixes stale-snapshot drift).
      // Wait long enough for FSI/PL hooks to recompute on the new formData.
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        // Reset debounce gate so the force-save isn't skipped
        lastSaveTimeRef.current = 0;
        lastSavedDataRef.current = null;
        autoSaveProject(true);
      }, 1500);
    } catch (error) {
      console.error("Error loading project:", error);
      toast.error("Failed to load project");
    }
  };

  // Delete a saved project
  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    
    try {
      await axios.delete(`${API}/saved-projects/${projectId}`);
      setSavedProjects(prev => prev.filter(p => p.id !== projectId));
      if (currentProjectId === projectId) {
        setCurrentProjectId(null);
      }
      toast.success("Project deleted");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  };

  // Admin-only: Batch-refresh computed_metrics snapshots for every owned saved project.
  // Programmatically loads each project into the live state so the on-load force-save
  // captures EXACT live-calc metrics (matching the Dashboard / P&L for every project).
  const [refreshingAll, setRefreshingAll] = useState(false);
  const handleRefreshAllSnapshots = useCallback(async () => {
    if (!savedProjects || savedProjects.length === 0) return;
    const ownedProjects = savedProjects.filter(p => p.is_owner !== false);
    if (ownedProjects.length === 0) {
      toast.error("No owned projects to refresh");
      return;
    }
    if (!window.confirm(`This will sequentially load each of your ${ownedProjects.length} projects to refresh its Comparison snapshot. The screen will briefly cycle through projects. Continue?`)) {
      return;
    }
    // Snapshot original state to restore after batch
    const originalFormData = formDataRef.current;
    const originalProjectId = currentProjectIdRef.current;

    setRefreshingAll(true);
    setLoadDialogOpen(false);
    const toastId = toast.loading(`Refreshing snapshots... 0 / ${ownedProjects.length}`);
    let succeeded = 0;
    let failed = 0;

    for (let i = 0; i < ownedProjects.length; i++) {
      const proj = ownedProjects[i];
      try {
        const resp = await axios.get(`${API}/saved-projects/${proj.id}`);
        const loadedData = resp.data.form_data;
        // Backward-compat fixes (mirror handleLoadProject)
        if (!loadedData.competitors || !Array.isArray(loadedData.competitors)) {
          loadedData.competitors = [{ ...emptyCompetitor }];
        }
        loadedData.competitors = loadedData.competitors.map(c => ({ ...c, bettermentChargesType: c.bettermentChargesType || "per_member" }));
        if (loadedData.awardedToCompetitorIndex === undefined) loadedData.awardedToCompetitorIndex = 0;

        // Set state so live hooks recompute fsiBreakdown / fsiCosts / daCharges / society / plData
        setFormData(loadedData);
        setCurrentProjectId(proj.id);

        // Wait long enough for the chain of useMemos / useEffects to settle
        await new Promise(r => setTimeout(r, 2200));

        // Force a save now — autoSaveProject reads plDataRef.current (latest live metrics)
        lastSaveTimeRef.current = 0;
        lastSavedDataRef.current = null;
        await autoSaveProject(true);
        await new Promise(r => setTimeout(r, 300));
        succeeded += 1;
      } catch (err) {
        console.error(`Refresh failed for ${proj.name}:`, err);
        failed += 1;
      }
      toast.loading(`Refreshing snapshots... ${i + 1} / ${ownedProjects.length}`, { id: toastId });
    }

    // Restore original state (or clear if user wasn't on a project)
    if (originalProjectId) {
      setFormData(originalFormData);
      setCurrentProjectId(originalProjectId);
    } else {
      setFormData({ ...defaultFormData, competitors: [createCompetitor()] });
      setCurrentProjectId(null);
    }

    setRefreshingAll(false);
    if (failed === 0) {
      toast.success(`Refreshed ${succeeded} project${succeeded === 1 ? '' : 's'} successfully`, { id: toastId });
    } else {
      toast.error(`Refreshed ${succeeded}, failed ${failed}`, { id: toastId });
    }
  }, [savedProjects, autoSaveProject]);

  // Duplicate current project as a new variant
  const handleDuplicateProject = () => {
    const originalName = formData.societyName || formData.projectName || "Project";
    const variantName = `${originalName} - Variant`;
    setFormData(prev => ({
      ...prev,
      societyName: variantName,
      projectName: variantName,
    }));
    setCurrentProjectId(null);
    lastSaveTimeRef.current = 0;
    lastSavedDataRef.current = null;
    setLastAutoSaveTime(null);
    setActiveTab("quickinput");
    toast.success(`Duplicated as "${variantName}" — change classification, FSI & free area as needed`);
  };

  // Duplicate a saved project from the Load dialog
  const handleDuplicateFromSaved = async (projectId, e) => {
    e.stopPropagation();
    try {
      const response = await axios.get(`${API}/saved-projects/${projectId}`);
      const loadedData = response.data.form_data;

      // Backward compat for competitors
      if (!loadedData.competitors || !Array.isArray(loadedData.competitors)) {
        loadedData.competitors = [{ ...emptyCompetitor }];
      }
      loadedData.competitors = loadedData.competitors.map(comp => ({
        ...comp,
        bettermentChargesType: comp.bettermentChargesType || "per_member"
      }));
      if (loadedData.awardedToCompetitorIndex === undefined) {
        loadedData.awardedToCompetitorIndex = 0;
      }

      const originalName = loadedData.societyName || loadedData.projectName || response.data.name;
      const variantName = `${originalName} - Variant`;
      loadedData.societyName = variantName;
      loadedData.projectName = variantName;

      setFormData(loadedData);
      setCurrentProjectId(null);
      lastSaveTimeRef.current = 0;
      lastSavedDataRef.current = null;
      setLastAutoSaveTime(null);
      setFSIBreakdown(null);
      setFSICosts(null);
      setSocietyCosts(null);
      setDAChargesCosts(null);
      setLoadDialogOpen(false);
      setActiveTab("quickinput");
      toast.success(`Duplicated "${response.data.name}" as variant — change classification, FSI & free area as needed`);
    } catch (error) {
      console.error("Error duplicating project:", error);
      toast.error("Failed to duplicate project");
    }
  };

  // New project (clear form)
  const handleNewProject = () => {
    if (window.confirm("Start a new project? Unsaved changes will be lost.")) {
      setFormData({
        ...defaultFormData,
        competitors: [createCompetitor()]
      });
      setCurrentProjectId(null);
      setFSIBreakdown(null);
      setFSICosts(null);
      setSocietyCosts(null);
      setDAChargesCosts(null);
      setResults(null);
      setLastOpenedInfo(null);
      setActiveTab("quickinput");
      toast.success("New project started");
    }
  };

  // Use extracted hook for export/import
  const { handleExportProject, handleImportProject } = useExportImport(
    formData,
    setFormData,
    setCurrentProjectId,
    setActiveTab
  );

  // Use extracted hook for project sharing
  const {
    shareDialogOpen,
    setShareDialogOpen,
    shareEmail,
    setShareEmail,
    copyEmail,
    setCopyEmail,
    shareLoading,
    copyLoading,
    sharedUsers,
    isProjectOwner,
    setIsProjectOwner,
    projectOwner,
    fetchSharedUsers,
    handleShareProject,
    handleSendCopy,
    handleRemoveSharedUser
  } = useProjectSharing(currentProjectId);

  // Load projects when dialog opens
  useEffect(() => {
    if (loadDialogOpen) {
      fetchSavedProjects();
    }
  }, [loadDialogOpen]);

  // Auto-set inputs when switching to Greenfield mode
  // RR rates remain; only clear existing area & old road widening
  useEffect(() => {
    if (formData.projectMode === "greenfield") {
      setFormData(prev => ({
        ...prev,
        hasOldRoadWidening: "no",
        oldRoadWideningArea: "0"
      }));
    }
  }, [formData.projectMode]);

  // Calculate society costs automatically for Redevelopment projects
  // This ensures P&L calculations work even if user skips the Society tab
  useEffect(() => {
    if (formData.projectMode === "greenfield") {
      // Set society costs to zero for Greenfield/JV projects
      setSocietyCosts({
        shiftingCharges: 0,
        corpusCharges: 0,
        bettermentCharges: 0,
        totalRentalCost: 0,
        depositCost: 0,
        brokerageCost: 0,
        pmcCost: 0,
        bgCost: 0,
        totalGST: 0,
        totalSocietyExpenses: 0,
        isGreenfield: true,
        jvType: formData.jvType || "area_sharing",
        jvRatioPercent: parseFloat(formData.jvRatioPercent) || 30
      });
    } else {
      // Calculate society costs for Redevelopment mode
      const conversionFactor = 0.092903;
      const areaFlatsValue = parseFloat(formData.areaFlats) || 0;
      const areaOfficesValue = parseFloat(formData.areaOffices) || 0;
      const areaShopsValue = parseFloat(formData.areaShops) || 0;
      const totalExistingAreaSft = areaFlatsValue + areaOfficesValue + areaShopsValue;
      const areaFlatsSqMts = areaFlatsValue * conversionFactor;
      const areaOfficesSqMts = areaOfficesValue * conversionFactor;
      const areaShopsSqMts = areaShopsValue * conversionFactor;
      
      const numMembers = parseInt(formData.numMembers) || 0;
      const duration = parseInt(formData.projectDuration) || 0;

      // 1. Shifting Charges
      const shiftingCharges = formData.includeShiftingCharges === false
        ? 0
        : numMembers * (parseFloat(formData.shiftingCharges) || 0);

      // 2. Corpus Charges - support per member or per sft
      let corpusCharges = 0;
      if (formData.includeCorpusCharges !== false) {
        if (formData.corpusChargesType === "per_sft") {
          corpusCharges = totalExistingAreaSft * (parseFloat(formData.corpusCharges) || 0);
        } else {
          corpusCharges = numMembers * (parseFloat(formData.corpusCharges) || 0);
        }
      }

      // 3. Betterment Charges - support per member or per sft
      let bettermentCharges = 0;
      if (formData.includeBettermentCharges !== false) {
        if (formData.bettermentChargesType === "per_sft") {
          bettermentCharges = totalExistingAreaSft * (parseFloat(formData.bettermentCharges) || 0);
        } else {
          bettermentCharges = numMembers * (parseFloat(formData.bettermentCharges) || 0);
        }
      }

      // 4. Rental Costs - supports per sft or lumpsum mode
      const rentType = formData.rentType || "per_sft";
      const rentIncrementPA = parseFloat(formData.rentIncrementPA) || 5;
      const shopRentDuration = parseInt(formData.shopRentDuration) || duration;
      const shopRentIncrement = parseFloat(formData.shopRentIncrement) || rentIncrementPA;
      const durationYears = Math.ceil(duration / 12);
      
      let monthlyRentFlats = 0;
      let monthlyRentOffices = 0;
      let monthlyRentShops = 0;
      let baseMonthlyRent = 0;
      
      if (rentType === "per_number" || rentType === "lumpsum") {
        // Lumpsum rent per configuration
        const config1BHK = parseInt(formData.config1BHK) || 0;
        const config2BHK = parseInt(formData.config2BHK) || 0;
        const config3BHK = parseInt(formData.config3BHK) || 0;
        const configShops = parseInt(formData.configShops) || 0;
        const configOthers = parseInt(formData.configOthers) || 0;
        
        const rentLumpsum1BHK = parseFloat(formData.rentLumpsum1BHK) || 0;
        const rentLumpsum2BHK = parseFloat(formData.rentLumpsum2BHK) || 0;
        const rentLumpsum3BHK = parseFloat(formData.rentLumpsum3BHK) || 0;
        const rentLumpsumShops = parseFloat(formData.rentLumpsumShops) || 0;
        const rentLumpsumOthers = parseFloat(formData.rentLumpsumOthers) || 0;
        
        // Monthly rent = count × lumpsum per unit
        monthlyRentFlats = (config1BHK * rentLumpsum1BHK) + (config2BHK * rentLumpsum2BHK) + (config3BHK * rentLumpsum3BHK) + (configOthers * rentLumpsumOthers);
        monthlyRentShops = configShops * rentLumpsumShops;
        monthlyRentOffices = 0; // Offices handled in flats for lumpsum
        baseMonthlyRent = monthlyRentFlats;
      } else {
        // Per sft rent
        const rentPerSqftFlat = parseFloat(formData.rentPerSqftFlat) || 0;
        const rentPerSqftOffice = parseFloat(formData.rentPerSqftOffice) || 0;
        const rentPerSqftShop = parseFloat(formData.rentPerSqftShop) || 0;
        
        monthlyRentFlats = areaFlatsValue * rentPerSqftFlat;
        monthlyRentOffices = areaOfficesValue * rentPerSqftOffice;
        monthlyRentShops = areaShopsValue * rentPerSqftShop;
        baseMonthlyRent = monthlyRentFlats + monthlyRentOffices;
      }
      
      let totalRentalCostFlatsOffices = 0;
      let totalRentalCostShops = 0;
      if (formData.includeRent !== false) {
        for (let year = 0; year < durationYears; year++) {
          const monthsInThisYear = year === durationYears - 1 ? 
            (duration % 12 === 0 ? 12 : duration % 12) : 12;
          const yearlyMultiplier = Math.pow(1 + rentIncrementPA / 100, year);
          totalRentalCostFlatsOffices += (monthlyRentFlats + monthlyRentOffices) * yearlyMultiplier * monthsInThisYear;
        }

        const shopDurationYears = Math.ceil(shopRentDuration / 12);
        for (let year = 0; year < shopDurationYears; year++) {
          const monthsInThisYear = year === shopDurationYears - 1 ? 
            (shopRentDuration % 12 === 0 ? 12 : shopRentDuration % 12) : 12;
          const yearlyMultiplier = Math.pow(1 + shopRentIncrement / 100, year);
          totalRentalCostShops += monthlyRentShops * yearlyMultiplier * monthsInThisYear;
        }
      } else {
        // Rent disabled — zero out monthly rents so deposit/brokerage (derived from rent) also become zero
        monthlyRentFlats = 0;
        monthlyRentOffices = 0;
        monthlyRentShops = 0;
        baseMonthlyRent = 0;
      }

      const totalRentalCost = totalRentalCostFlatsOffices + totalRentalCostShops;

      // 5. Refundable Deposit
      let depositCost = 0;
      if (formData.refundableDeposit === "yes") {
        const depositMonths = parseInt(formData.depositMonths) || 0;
        depositCost = baseMonthlyRent * depositMonths;
      }

      // 6. Brokerage
      let brokerageCost = 0;
      if (formData.brokeragePayable === "yes") {
        const brokerageMonths = parseInt(formData.brokerageMonths) || 0;
        brokerageCost = baseMonthlyRent * brokerageMonths;
      }

      // 7. PMC Charges
      let pmcCost = 0;
      if (formData.societyPMCPayable === "yes") {
        pmcCost = parseFloat(formData.pmcAmount) || 0;
      }

      // 8. Bank Guarantee Cost = Amount × Duration × Rate% p.a.
      let bgCost = 0;
      if (formData.bankGuaranteeRequired === "yes" || formData.bankGuaranteeRequired === true) {
        const bgAmount = parseFloat(formData.bankGuaranteeAmount) || 0;
        const bgRatePA = parseFloat(formData.bgRatePA) || 6;
        const bgDurationYears = Math.ceil(duration / 12);
        bgCost = bgAmount * bgDurationYears * (bgRatePA / 100);
      }

      // 9. GST Calculation
      // Residential (flats) → 5% GST
      // Commercial (offices + shops) → 12% GST minus editable offset (default 5%) = net 7%
      const commercialGstOffset = parseFloat(formData.commercialGSTOffset);
      const commercialGSTOffsetPct = isNaN(commercialGstOffset) ? 5 : commercialGstOffset;
      const commercialGstRate = Math.max(0, (12 - commercialGSTOffsetPct)) / 100;
      const rrMultiplierPercent = parseFloat(formData.rrMultiplier) || 5;
      const rrMultiplierFactor = 1 + (rrMultiplierPercent / 100);
      
      const rrFlat = (parseFloat(formData.rrRateFlat) || 0) * rrMultiplierFactor;
      const rrShop = ((parseFloat(formData.rrRateShop) || parseFloat(formData.rrRateFlat) || 0)) * rrMultiplierFactor;
      const additionalFreePercent = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
      
      const flatFreeArea = areaFlatsSqMts * (additionalFreePercent / 100);
      const gstFlats = formData.hasFlats ? (areaFlatsSqMts + flatFreeArea) * 1.1 * rrFlat * 0.05 : 0;
      
      const shopFreeArea = areaShopsSqMts * (additionalFreePercent / 100);
      const gstShops = formData.hasShops ? (areaShopsSqMts + shopFreeArea) * 1.1 * rrShop * commercialGstRate : 0;
      
      const officeFreeArea = areaOfficesSqMts * (additionalFreePercent / 100);
      const gstOffices = formData.hasOffices ? (areaOfficesSqMts + officeFreeArea) * 1.1 * rrFlat * commercialGstRate : 0;
      
      const totalGST = gstFlats + gstShops + gstOffices;

      // 10. Parking Compensation Payout
      const parkingCompensationPayout = formData.parkingCompensationType === "payout" 
        ? (parseFloat(formData.compensationPayout) || 0) * (parseInt(formData.existingParkings) || 0)
        : 0;

      // Total Society Expenses (deposit excluded - it is refundable, not an expense)
      const totalSocietyExpenses = shiftingCharges + corpusCharges + bettermentCharges + 
                                    totalRentalCost + brokerageCost + 
                                    pmcCost + bgCost + totalGST + parkingCompensationPayout;

      setSocietyCosts({
        shiftingCharges,
        corpusCharges,
        bettermentCharges,
        totalRentalCost,
        totalRentalCostFlatsOffices,
        totalRentalCostShops,
        monthlyRentFlats,
        monthlyRentOffices,
        monthlyRentShops,
        parkingCompensationPayout,
        shopRentDuration,
        depositCost,
        brokerageCost,
        pmcCost,
        bgCost,
        gstFlats,
        gstOffices,
        gstShops,
        totalGST,
        totalSocietyExpenses,
        isGreenfield: false
      });
    }
  }, [
    formData.projectMode,
    formData.numMembers,
    formData.projectDuration,
    formData.shiftingCharges,
    formData.corpusCharges,
    formData.corpusChargesType,
    formData.bettermentCharges,
    formData.bettermentChargesType,
    formData.areaFlats,
    formData.areaOffices,
    formData.areaShops,
    formData.rentPerSqftFlat,
    formData.rentPerSqftOffice,
    formData.rentPerSqftShop,
    formData.rentIncrementPA,
    formData.shopRentDuration,
    formData.shopRentIncrement,
    formData.refundableDeposit,
    formData.depositMonths,
    formData.brokeragePayable,
    formData.brokerageMonths,
    formData.societyPMCPayable,
    formData.pmcAmount,
    formData.bankGuaranteeRequired,
    formData.bankGuaranteeAmount,
    formData.bgRatePA,
    formData.rrRateFlat,
    formData.rrRateShop,
    formData.rrMultiplier,
    formData.additionalFreeAreaPercent,
    formData.hasFlats,
    formData.hasOffices,
    formData.hasShops,
    formData.parkingCompensationType,
    formData.compensationPayout,
    formData.jvRatioPercent,
    formData.jvType,
    formData.rentType,
    formData.config1BHK,
    formData.config2BHK,
    formData.config3BHK,
    formData.configShops,
    formData.configOthers,
    formData.rentLumpsum1BHK,
    formData.rentLumpsum2BHK,
    formData.rentLumpsum3BHK,
    formData.rentLumpsumShops,
    formData.rentLumpsumOthers,
    formData.includeRent,
    formData.includeCorpusCharges,
    formData.includeBettermentCharges,
    formData.includeShiftingCharges,
    formData.commercialGSTOffset,
    formData.projectMode
  ]);

  // Auto-compute FSI breakdown via shared util (single source of truth in calculations.js)
  useEffect(() => {
    const fb = computeFSIBreakdownFromFormData(formData);
    if (fb) setFSIBreakdown(fb);
  }, [formData]);

  // Auto-compute FSI costs when fsiBreakdown changes (ensures cash flow has data even without visiting FSI tab)
  useEffect(() => {
    if (!fsiBreakdown || !formData.rrRatePlot || !formData.rrRateConstruction) return;
    // Skip if already computed by the FSISanctionCosts component
    if (fsiCosts && fsiCosts._fromComponent) return;

    const rrMultiplierPercent = parseFloat(formData.rrMultiplier) || 5;
    const rrMultiplierFactor = 1 + (rrMultiplierPercent / 100);
    const rrAdditionalMarkup = formData.applyRRMarkup === "yes" ? (parseFloat(formData.rrAdditionalMarkupPercent) || 0) : 0;
    const rrMarkupFactor = 1 + (rrAdditionalMarkup / 100);
    const rrRatePlot = (parseFloat(formData.rrRatePlot) || 0) * rrMultiplierFactor * rrMarkupFactor;
    const rrRateConstruction = (parseFloat(formData.rrRateConstruction) || 0) * rrMultiplierFactor * rrMarkupFactor;
    const isCommercial = formData.projectType === "commercial";
    const commercialPercentage = parseFloat(formData.commercialPercentage) || 0;
    const numMembers = parseInt(formData.numMembers) || 0;
    const plotAreaSqMtr = formData.plotAreaUnit === "sqft"
      ? (parseFloat(formData.plotAreaPerCard) || 0) * 0.092903
      : (parseFloat(formData.plotAreaPerCard) || 0);

    const isMhada = formData.projectClassification === "mhada";

    let totalCosts, ancillaryFSICost, paidFSICost, cbdPremiumCost = 0, amenityTDRCost = 0, slumTDRCost = 0, 
        landDevCharges = 0, buildingDevCharges = 0, upkar = 0, fireFightingCharges = 0, ulcCharges = 0;

    if (isMhada) {
      const mhadaTotalFSI = fsiBreakdown?.effectiveFSI || 3;
      const purchasableFSI = mhadaTotalFSI - 1.1;
      const mhadaFSIArea = plotAreaSqMtr * purchasableFSI;
      const mhadaFSICost = mhadaFSIArea * rrRatePlot * 0.45;
      const proRataFSI = numMembers * 26.2;
      ancillaryFSICost = fsiBreakdown.totalAncillary * rrRatePlot * 0.15;
      paidFSICost = 0;
      const mhadaNumBuildings = parseInt(formData.mhadaNumBuildings) || 1;
      const totalFSIForInfra = plotAreaSqMtr * mhadaTotalFSI + proRataFSI + fsiBreakdown.totalAncillary;
      const mhadaInfrastructure = totalFSIForInfra * rrRatePlot * 0.07 * 0.50;
      const mhadaSanctionRate = parseFloat(formData.mhadaSanctionRate) || 300;
      const plotAreaSqft = plotAreaSqMtr / 0.092903;
      const mhadaSanctionCost = plotAreaSqft * 2 * mhadaSanctionRate;
      const scrutinyCharges = totalFSIForInfra * 5;
      const radaRodaCharges = totalFSIForInfra * 20;
      const existingBuiltupArea = parseFloat(formData.existingBuiltupArea) || plotAreaSqMtr;
      // Greenfield has no existing building → no demolition
      const demolitionCharges = formData.projectMode === "greenfield" ? 0 : (existingBuiltupArea * 50 / 1000 * 227);
      const waterLineCharges = Math.sqrt(plotAreaSqMtr) * 3640;
      const cdCharges = totalFSIForInfra / 1000 * 227;
      const fourWheelerPerMember = parseFloat(formData.fourWheelerPerMember) || 1;
      const twoWheelerPerMember = parseFloat(formData.twoWheelerPerMember) || 1;
      const nonFSIArea = (numMembers * fourWheelerPerMember * 20) + (numMembers * twoWheelerPerMember * 4);
      const nonFSICharges = nonFSIArea * 0.01 * rrRateConstruction;
      if (formData.ulcSection20 === "yes") {
        ulcCharges = (parseFloat(formData.ulcArea) || plotAreaSqMtr) * rrRatePlot * 0.025;
      }
      totalCosts = mhadaFSICost + ancillaryFSICost + 27000 * mhadaNumBuildings + 6600 * mhadaNumBuildings +
                   1000 * numMembers + 100000 * mhadaNumBuildings + mhadaInfrastructure + mhadaInfrastructure +
                   mhadaSanctionCost + scrutinyCharges + radaRodaCharges + demolitionCharges +
                   waterLineCharges + cdCharges + nonFSICharges + ulcCharges;
    } else {
      let adjustedPaidFSIArea = fsiBreakdown.paidFSIArea;
      let adjustedTDRArea = fsiBreakdown.tdrArea;
      const yearBuilt = parseInt(formData.yearBuilt) || 1980;
      const buildingAge = new Date().getFullYear() - yearBuilt;
      let incentiveFSI = 0;
      const originalAmenityTDRArea = fsiBreakdown.amenityTDR * fsiBreakdown.grossPlotArea;
      const originalSlumTDRArea = fsiBreakdown.slumTDR * fsiBreakdown.grossPlotArea;
      let amenityTDRAreaAfterIncentive = originalAmenityTDRArea;
      let slumTDRAreaAfterIncentive = originalSlumTDRArea;
      
      // Check for narrow road (< 9m)
      const isNarrowRoad = fsiBreakdown.isNarrowRoad || false;
      
      if (isNarrowRoad) {
        // Narrow road: use backend-calculated incentive FSI, no TDR/Paid FSI
        incentiveFSI = fsiBreakdown.incentiveFSIArea || 0;
        adjustedPaidFSIArea = 0;
        adjustedTDRArea = 0;
        amenityTDRAreaAfterIncentive = 0;
        slumTDRAreaAfterIncentive = 0;
      } else if (buildingAge > 30) {
        incentiveFSI = fsiBreakdown.grossPlotArea * 0.30;
        const totalPaidTDR = fsiBreakdown.paidFSI + fsiBreakdown.totalTDR;
        if (totalPaidTDR > 0) {
          const paidP = fsiBreakdown.paidFSI / totalPaidTDR;
          const tdrP = fsiBreakdown.totalTDR / totalPaidTDR;
          adjustedPaidFSIArea = Math.max(0, fsiBreakdown.paidFSIArea - incentiveFSI * paidP);
          adjustedTDRArea = Math.max(0, fsiBreakdown.tdrArea - incentiveFSI * tdrP);
          const totalOrigTDR = originalAmenityTDRArea + originalSlumTDRArea;
          if (totalOrigTDR > 0) {
            amenityTDRAreaAfterIncentive = Math.max(0, originalAmenityTDRArea - (incentiveFSI * tdrP * originalAmenityTDRArea / totalOrigTDR));
            slumTDRAreaAfterIncentive = Math.max(0, originalSlumTDRArea - (incentiveFSI * tdrP * originalSlumTDRArea / totalOrigTDR));
          }
        }
      }
      const tdrReductionFromRoadWidening = fsiBreakdown.tdrReductionFromRoadWidening || 0;
      const amenityTDRPercent = parseFloat(formData.amenityTDRPercent) || 0;
      const adjustedAmenityTDRArea = Math.max(0, amenityTDRAreaAfterIncentive - tdrReductionFromRoadWidening);
      amenityTDRCost = adjustedAmenityTDRArea * rrRatePlot * (amenityTDRPercent / 100);
      if (formData.slumTDRMethod === "zone_drc") {
        slumTDRCost = slumTDRAreaAfterIncentive * (parseFloat(formData.slumTDRZoneDRC) || 0) * 10.764;
      } else {
        // Rate method: user enters rate in ₹/sft, convert to per sq mtr
        const ratePerSft = parseFloat(formData.slumTDRRRPercent) || 0;
        slumTDRCost = slumTDRAreaAfterIncentive * (ratePerSft * 10.764);
      }
      ancillaryFSICost = fsiBreakdown.totalAncillary * rrRatePlot * 0.15;
      paidFSICost = adjustedPaidFSIArea * rrRatePlot * 0.35;
      // CBD Premium FSI (only when classification === 'cbd')
      const cbdPremiumArea = fsiBreakdown.cbdPremiumArea || 0;
      cbdPremiumCost = cbdPremiumArea * rrRatePlot * 0.50;
      const landDevRate = isCommercial ? 0.02 : (0.01 + (commercialPercentage / 100) * 0.01);
      landDevCharges = fsiBreakdown.grossPlotArea * rrRatePlot * landDevRate;
      const buildingDevRate = isCommercial ? 0.08 : (0.04 + (commercialPercentage / 100) * 0.04);
      buildingDevCharges = fsiBreakdown.totalFSIWithAncillary * rrRatePlot * buildingDevRate;
      const labourCessRate = isCommercial ? 0.015 : (0.01 + (commercialPercentage / 100) * 0.005);
      upkar = fsiBreakdown.totalFSIWithAncillary * rrRateConstruction * labourCessRate;
      fireFightingCharges = fsiBreakdown.totalFSIWithAncillary * (parseFloat(formData.fireFightingRate) || 0);
      const scrutinyCharges = fsiBreakdown.totalFSIWithAncillary * 5;
      const radaRodaCharges = fsiBreakdown.totalFSIWithAncillary * 20;
      const existingBuiltupArea = parseFloat(formData.existingBuiltupArea) || parseFloat(formData.plotAreaPerCard) || 0;
      // Greenfield has no existing building → no demolition
      const demolitionCharges = formData.projectMode === "greenfield" ? 0 : (existingBuiltupArea * 50 / 1000 * 227);
      const waterLineCharges = Math.sqrt(plotAreaSqMtr) * 3640;
      const cdCharges = fsiBreakdown.totalFSIWithAncillary * 0.05 * 227;
      const fourWheelerPerMember = parseFloat(formData.fourWheelerPerMember) || 1;
      const twoWheelerPerMember = parseFloat(formData.twoWheelerPerMember) || 1;
      const nonFSIArea = (numMembers * fourWheelerPerMember * 20) + (numMembers * twoWheelerPerMember * 4);
      const nonFSICharges = nonFSIArea * 0.01 * rrRateConstruction;
      if (formData.ulcSection20 === "yes") {
        ulcCharges = (parseFloat(formData.ulcArea) || plotAreaSqMtr) * rrRatePlot * 0.025;
      }
      totalCosts = amenityTDRCost + slumTDRCost + ancillaryFSICost + paidFSICost + cbdPremiumCost +
                   landDevCharges + buildingDevCharges + upkar + fireFightingCharges +
                   scrutinyCharges + radaRodaCharges + demolitionCharges + waterLineCharges +
                   cdCharges + nonFSICharges + ulcCharges;
    }

    setFSICosts(prev => {
      if (prev && prev._fromComponent) return prev;
      return { 
        totalCosts, ancillaryFSICost, paidFSICost, cbdPremiumCost, amenityTDRCost, slumTDRCost,
        landDevCharges, buildingDevCharges, upkar, fireFightingCharges, ulcCharges,
        _auto: true 
      };
    });
  }, [fsiBreakdown, formData]);

  // Auto-compute DA charges when dependencies change
  useEffect(() => {
    if (!fsiBreakdown) return;

    const isGreenfield = formData.projectMode === "greenfield";

    if (isGreenfield) {
      // Greenfield JV: Landowner's carpet area (JV %) treated as "existing area" for DA stamp duty
      const jvType = formData.jvType || "area_sharing";
      const jvRatio = parseFloat(formData.jvRatioPercent) || 30;
      const jvRatioFlats = parseFloat(formData.jvRatioFlats) || jvRatio;
      const jvRatioOffices = parseFloat(formData.jvRatioOffices) || jvRatio;
      const jvRatioShops = parseFloat(formData.jvRatioShops) || jvRatio;
      const conversionFactor = 0.092903;
      const totalFSIArea = fsiBreakdown.totalFSIWithAncillary || 0;
      const carpetFactor = parseFloat(formData.carpetAreaFactor) || 70;
      const totalCarpetArea = totalFSIArea * (carpetFactor / 100);
      
      // Landowner's carpet = per-type carpet weighted by that type's JV ratio
      // (Ensures offices-only projects use only the offices JV %, ignoring flats/shops defaults)
      const resPctGF = parseFloat(formData.residentialPercentage);
      const commPctGF = parseFloat(formData.commercialPercentage);
      const officesSplitGF = parseFloat(formData.saleableAreaSplitOffices) || 0;
      const shopsSplitGF = parseFloat(formData.saleableAreaSplitShops) || 0;
      const ptGF = formData.projectType;

      let flatsRatioGF = 0, officesRatioGF = 0, shopsRatioGF = 0;
      if (ptGF === "residential") {
        flatsRatioGF = 1;
      } else if (ptGF === "commercial") {
        const totalSplit = officesSplitGF + shopsSplitGF;
        if (totalSplit > 0) {
          officesRatioGF = officesSplitGF / totalSplit;
          shopsRatioGF = shopsSplitGF / totalSplit;
        } else {
          officesRatioGF = 1;
        }
      } else if (ptGF === "mixed") {
        flatsRatioGF = (isNaN(resPctGF) ? 60 : resPctGF) / 100;
        const commercialFrac = (isNaN(commPctGF) ? (100 - (isNaN(resPctGF) ? 60 : resPctGF)) : commPctGF) / 100;
        const totalSplit = officesSplitGF + shopsSplitGF;
        if (totalSplit > 0) {
          officesRatioGF = commercialFrac * (officesSplitGF / totalSplit);
          shopsRatioGF = commercialFrac * (shopsSplitGF / totalSplit);
        } else {
          officesRatioGF = commercialFrac;
        }
      } else {
        flatsRatioGF = 1;
      }

      const flatsCarpetGF = totalCarpetArea * flatsRatioGF;
      const officesCarpetGF = totalCarpetArea * officesRatioGF;
      const shopsCarpetGF = totalCarpetArea * shopsRatioGF;

      const landownerCarpetSqMts =
        flatsCarpetGF * (jvRatioFlats / 100) +
        officesCarpetGF * (jvRatioOffices / 100) +
        shopsCarpetGF * (jvRatioShops / 100);
      
      // Use landowner's carpet as "existing area" for DA calculation
      const existingCarpetSqMts = landownerCarpetSqMts;
      const rrConstruction = parseFloat(formData.rrRateConstruction) || 0;
      const rrPlot = parseFloat(formData.rrRatePlot) || 0;
      const tdrArea = fsiBreakdown?.tdrArea || 0;
      const paidFSIArea = fsiBreakdown?.paidFSIArea || 0;
      const ancillaryArea = fsiBreakdown?.totalAncillary || 0;
      
      // Option 1: Base value = Landowner carpet × 1.1 × Construction RR
      const baseValue = existingCarpetSqMts * 1.1 * rrConstruction;
      const developmentCharges = existingCarpetSqMts * 1.1 * 0.02 * rrPlot;
      const option1Total = baseValue + developmentCharges;
      
      // Option 2: FSI based
      const fsiMinusCarpet = totalFSIArea - (existingCarpetSqMts * 1.1);
      const option2Base = fsiMinusCarpet * rrPlot;
      const tdrDeduction = tdrArea * 0.30 * rrPlot;
      const paidFSIDeduction = paidFSIArea * 0.35 * rrPlot;
      const ancillaryDeduction = ancillaryArea * 0.15 * rrPlot;
      const option2Total = Math.max(0, option2Base - tdrDeduction - paidFSIDeduction - ancillaryDeduction);
      
      const higherOption = Math.max(option1Total, option2Total);
      const stampDutyRate = parseFloat(formData.stampDutyRate) || 5;
      const stampDuty = higherOption * (stampDutyRate / 100);
      const totalDACharges = stampDuty + 30000;

      setDAChargesCosts(prev => {
        if (prev && prev._fromComponent) return prev;
        return { totalDACharges, developmentCharges, stampDuty, totalRegistration: 30000, agreementValue: higherOption, landownerCarpetSqMts, _auto: true };
      });
      return;
    }

    // Redevelopment mode
    if (!societyCosts) return;

    const conversionFactor = 0.092903;
    const areaFlatsSqMts = (parseFloat(formData.areaFlats) || 0) * conversionFactor;
    const areaOfficesSqMts = (parseFloat(formData.areaOffices) || 0) * conversionFactor;
    const areaShopsSqMts = (parseFloat(formData.areaShops) || 0) * conversionFactor;
    const existingCarpetSqMts = areaFlatsSqMts + areaOfficesSqMts + areaShopsSqMts;
    
    let totalCarpetToSociety, additionalFreeCarpet, additionalFreePercent;
    const isDirectAreaMode = formData.areaOfferMode === "direct" && parseFloat(formData.directAreaOfferedSqft) > 0;
    
    if (isDirectAreaMode) {
      // Direct mode: total area is directly specified
      totalCarpetToSociety = parseFloat(formData.directAreaOfferedSqft) / 10.76;
      additionalFreeCarpet = totalCarpetToSociety - existingCarpetSqMts;
      additionalFreePercent = existingCarpetSqMts > 0 ? (additionalFreeCarpet / existingCarpetSqMts) * 100 : 0;
    } else {
      additionalFreePercent = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
      additionalFreeCarpet = existingCarpetSqMts * (additionalFreePercent / 100);
      const areaTerraceSqMts = (parseFloat(formData.areaTerrace) || 0) * conversionFactor;
      const areaOverheadTerraceSqMts = (parseFloat(formData.areaOverheadTerrace) || 0) * conversionFactor;
      const areaGardenSqMts = (parseFloat(formData.areaGarden) || 0) * conversionFactor;
      const terraceConversionRatio = parseFloat(formData.terraceConversionRatio) || 25;
      const overheadTerraceConversionRatio = parseFloat(formData.overheadTerraceConversionRatio) || 25;
      const gardenConversionRatio = parseFloat(formData.gardenConversionRatio) || 25;
      const convertedTerrace = areaTerraceSqMts * (terraceConversionRatio / 100);
      const convertedOverheadTerrace = areaOverheadTerraceSqMts * (overheadTerraceConversionRatio / 100);
      const convertedGarden = areaGardenSqMts * (gardenConversionRatio / 100);
      const applyIncrementOnTG = formData.applyIncrementOnTerraceGarden === "yes";
      const freePercentForIncrement = parseFloat(formData.freeAreaPercentFlats) || additionalFreePercent;
      const incrementOnTerrace = applyIncrementOnTG ? convertedTerrace * (freePercentForIncrement / 100) : 0;
      const incrementOnOverheadTerrace = applyIncrementOnTG ? convertedOverheadTerrace * (freePercentForIncrement / 100) : 0;
      const incrementOnGarden = applyIncrementOnTG ? convertedGarden * (freePercentForIncrement / 100) : 0;
      const totalTerraceGardenWithIncrement = convertedTerrace + convertedOverheadTerrace + convertedGarden + incrementOnTerrace + incrementOnOverheadTerrace + incrementOnGarden;
      totalCarpetToSociety = existingCarpetSqMts + additionalFreeCarpet + totalTerraceGardenWithIncrement;
    }
    const rrConstruction = parseFloat(formData.rrRateConstruction) || 0;
    const rrPlot = parseFloat(formData.rrRatePlot) || 0;
    const numMembers = parseInt(formData.numMembers) || 0;
    const durationMonths = parseInt(formData.projectDuration) || 0;
    const durationYears = durationMonths / 12;
    const totalFSI = fsiBreakdown?.totalFSIWithAncillary || 0;
    const tdrArea = fsiBreakdown?.tdrArea || 0;
    const paidFSIArea = fsiBreakdown?.paidFSIArea || 0;
    const ancillaryArea = fsiBreakdown?.totalAncillary || 0;
    const baseValue = totalCarpetToSociety * 1.1 * rrConstruction;
    const totalRent = societyCosts?.totalRentalCost || 0;
    const shiftingCharges = societyCosts?.shiftingCharges || 0;
    const corpusCharges = societyCosts?.corpusCharges || 0;
    const depositCost = societyCosts?.depositCost || 0;
    const bettermentCharges = societyCosts?.bettermentCharges || 0;
    const depositInterest = depositCost * durationYears * 0.06;
    const fourWheelerPerMember = parseFloat(formData.fourWheelerPerMember) || 1;
    const twoWheelerPerMember = parseFloat(formData.twoWheelerPerMember) || 1;
    const total4WheelerParking = numMembers * fourWheelerPerMember;
    const fourWheelerCost = total4WheelerParking * 12.5 * 0.25 * rrConstruction;
    const total2WheelerParking = numMembers * twoWheelerPerMember;
    const twoWheelerCost = total2WheelerParking * 2 * 0.25 * rrConstruction;
    const totalParkingCost = fourWheelerCost + twoWheelerCost;
    const developmentCharges = totalCarpetToSociety * 1.1 * 0.02 * rrPlot;
    const fungibleCost = isDirectAreaMode ? 0 : additionalFreeCarpet * 1.1 * 0.3 * rrPlot;
    const option1Total = baseValue + totalRent + shiftingCharges + corpusCharges + depositInterest + totalParkingCost + developmentCharges + fungibleCost + bettermentCharges;
    const terraceGardenForOption2 = isDirectAreaMode ? 0 : (totalCarpetToSociety - existingCarpetSqMts - additionalFreeCarpet);
    const fsiMinusCarpet = totalFSI - (existingCarpetSqMts * 1.1) - (additionalFreeCarpet * 1.1) - (terraceGardenForOption2 * 1.1);
    const option2Base = fsiMinusCarpet * rrPlot;
    const tdrDeduction = tdrArea * 0.30 * rrPlot;
    const paidFSIDeduction = paidFSIArea * 0.35 * rrPlot;
    const ancillaryDeduction = ancillaryArea * 0.15 * rrPlot;
    const option2Total = Math.max(0, option2Base - tdrDeduction - paidFSIDeduction - ancillaryDeduction);
    const higherOption = Math.max(option1Total, option2Total);
    const stampDuty = higherOption * 0.05;
    const totalDACharges = stampDuty + 30000;

    setDAChargesCosts(prev => {
      if (prev && prev._fromComponent) return prev;
      return { totalDACharges, developmentCharges, stampDuty, totalRegistration: 30000, _auto: true };
    });
  }, [fsiBreakdown, societyCosts, formData]);



  // Memoized plData for Dashboard and PDF export
  const plData = useMemo(() => {
    const baseMetrics = calculateOurOfferMetrics(formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, appConfig);
    if (!baseMetrics) return null;
    
    // Use canonical initial investment from shared calculations (single source of truth)
    return {
      ...baseMetrics,
      netProfit: baseMetrics.grossProfit,
    };
  }, [formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, appConfig]);

  // Keep plDataRef in sync with the latest plData so autoSaveProject (memoized) can read fresh metrics
  useEffect(() => { plDataRef.current = plData; }, [plData]);

  // Memoized competitor comparison data for PDF export
  const competitorData = useMemo(() => {
    // Use first competitor from the array if available
    const firstCompetitor = formData.competitors?.[0];
    if (!fsiBreakdown || !firstCompetitor || (!firstCompetitor.additionalAreaPercent && !firstCompetitor.areaSharePercent)) {
      return null;
    }

    const conversionFactor = 0.092903;
    const totalFSIArea = fsiBreakdown.totalFSIWithAncillary || 0;
    const carpetFactor = parseFloat(formData.carpetAreaFactor) || 70;
    const totalCarpetArea = totalFSIArea * (carpetFactor / 100);
    
    // Existing areas
    const existingFlats = (parseFloat(formData.areaFlats) || 0) * conversionFactor;
    const existingShops = (parseFloat(formData.areaShops) || 0) * conversionFactor;
    const existingOffices = (parseFloat(formData.areaOffices) || 0) * conversionFactor;
    const existingCarpetArea = existingFlats + existingOffices + existingShops;
    
    // OUR CALCULATIONS
    const ourFreeAreaPercent = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
    const ourAdditionalFree = existingCarpetArea * (ourFreeAreaPercent / 100);
    const areaTerrace = (parseFloat(formData.areaTerrace) || 0) * conversionFactor;
    const areaOverheadTerrace = (parseFloat(formData.areaOverheadTerrace) || 0) * conversionFactor;
    const areaGarden = (parseFloat(formData.areaGarden) || 0) * conversionFactor;
    const terraceConversion = parseFloat(formData.terraceConversionRatio) || 25;
    const overheadTerraceConversion = parseFloat(formData.overheadTerraceConversionRatio) || 25;
    const gardenConversion = parseFloat(formData.gardenConversionRatio) || 25;
    const convertedTerrace = areaTerrace * terraceConversion / 100;
    const convertedOverheadTerrace = areaOverheadTerrace * overheadTerraceConversion / 100;
    const convertedGarden = areaGarden * gardenConversion / 100;
    const convertedTerraceGarden = convertedTerrace + convertedOverheadTerrace + convertedGarden;
    // Apply increment on terrace/garden if toggle is yes
    const applyIncrementOnTG = formData.applyIncrementOnTerraceGarden === "yes";
    const incrementOnTG = applyIncrementOnTG ? convertedTerraceGarden * (ourFreeAreaPercent / 100) : 0;
    const ourTotalFreeArea = ourAdditionalFree + convertedTerraceGarden + incrementOnTG;
    const ourBalanceCarpet = Math.max(0, totalCarpetArea - existingCarpetArea - ourTotalFreeArea);
    const loadingPct = parseFloat(formData.loadingPercentage) || 40;
    const ourSaleableAreaSqft = (ourBalanceCarpet * (1 + loadingPct / 100)) / conversionFactor;
    const ourSocietyShare = totalCarpetArea > 0 ? ((existingCarpetArea + ourTotalFreeArea) / totalCarpetArea) * 100 : 0;
    const ourDeveloperShare = 100 - ourSocietyShare;
    
    // COMPETITOR CALCULATIONS (use first competitor from array)
    const compFreeAreaPercent = parseFloat(firstCompetitor.additionalAreaPercent) || 0;
    const compAreaSharingPercent = parseFloat(firstCompetitor.areaSharePercent) || 0;
    const compAdditionalFree = existingCarpetArea * (compFreeAreaPercent / 100);
    let compBalanceCarpet = Math.max(0, totalCarpetArea - existingCarpetArea - compAdditionalFree);
    let compSocietyShare = 0, compDeveloperShare = 0;
    
    if (compAreaSharingPercent > 0) {
      compSocietyShare = compAreaSharingPercent;
      compDeveloperShare = 100 - compAreaSharingPercent;
      compBalanceCarpet = (totalCarpetArea - existingCarpetArea) * (compDeveloperShare / 100);
    } else {
      compSocietyShare = totalCarpetArea > 0 ? ((existingCarpetArea + compAdditionalFree) / totalCarpetArea) * 100 : 0;
      compDeveloperShare = 100 - compSocietyShare;
    }
    const compSaleableAreaSqft = (compBalanceCarpet * (1 + loadingPct / 100)) / conversionFactor;
    
    // Revenue
    const saleRate = parseFloat(formData.saleRatePerSqft) || 0;
    const ourRevenue = ourSaleableAreaSqft * saleRate;
    const compRevenue = compSaleableAreaSqft * saleRate;
    
    // Our Society Expenses
    const ourSocietyExpenses = societyCosts?.totalSocietyExpenses || 0;
    
    // Competitor Society Expenses (simplified calculation)
    const numMembers = parseInt(formData.numMembers) || 0;
    const compDuration = parseInt(firstCompetitor.projectDuration) || parseInt(formData.projectDuration) || 48;
    const compDurationYears = compDuration / 12;
    
    const compShifting = (parseFloat(firstCompetitor.shiftingCharges) || 0) * numMembers;
    const compCorpus = (parseFloat(firstCompetitor.corpusCharges) || 0) * numMembers;
    let compBetterment = 0;
    const compBettermentType = firstCompetitor.bettermentChargesType || "per_member";
    if (compBettermentType === "per_member") {
      compBetterment = (parseFloat(firstCompetitor.bettermentCharges) || 0) * numMembers;
    } else {
      compBetterment = (parseFloat(firstCompetitor.bettermentCharges) || 0) * (parseFloat(formData.areaFlats) || 0);
    }
    
    // Simplified rent calculation
    const compRentFlat = parseFloat(firstCompetitor.rentPerSqftFlat) || 0;
    const compRentShop = parseFloat(firstCompetitor.rentPerSqftShop) || 0;
    const existingFlatsSqft = parseFloat(formData.areaFlats) || 0;
    const existingShopsSqft = parseFloat(formData.areaShops) || 0;
    const existingOfficesSqft = parseFloat(formData.areaOffices) || 0;
    const compTotalRent = ((existingFlatsSqft + existingOfficesSqft) * compRentFlat + existingShopsSqft * compRentShop) * compDuration;
    
    // GST Calculation — flats 5%, commercial (offices+shops) 12% − editable offset (default 5%) = 7%
    const _cgo1 = parseFloat(formData.commercialGSTOffset);
    const commercialGSTOffsetPct1 = isNaN(_cgo1) ? 5 : _cgo1;
    const commercialGstRate1 = Math.max(0, (12 - commercialGSTOffsetPct1)) / 100;
    const rrMultiplierPercent = parseFloat(formData.rrMultiplier) || 5;
    const rrMultiplierFactor = 1 + (rrMultiplierPercent / 100);
    const rrFlat = (parseFloat(formData.rrRateFlat) || 0) * rrMultiplierFactor;
    const rrShop_ = ((parseFloat(formData.rrRateShop) || parseFloat(formData.rrRateFlat) || 0)) * rrMultiplierFactor;
    const existingFlatsSqMts = existingFlatsSqft * conversionFactor;
    const existingShopsSqMts = existingShopsSqft * conversionFactor;
    const existingOfficesSqMts = existingOfficesSqft * conversionFactor;
    const compFlatFreeArea = existingFlatsSqMts * (compFreeAreaPercent / 100);
    const compShopFreeArea = existingShopsSqMts * (compFreeAreaPercent / 100);
    const compOfficeFreeArea = existingOfficesSqMts * (compFreeAreaPercent / 100);
    const compGstFlats = (existingFlatsSqMts + compFlatFreeArea) * 1.1 * rrFlat * 0.05;
    const compGstShops = (existingShopsSqMts + compShopFreeArea) * 1.1 * rrShop_ * commercialGstRate1;
    const compGstOffices = (existingOfficesSqMts + compOfficeFreeArea) * 1.1 * rrFlat * commercialGstRate1;
    const compTotalGST = compGstFlats + compGstShops + compGstOffices;
    
    const compSocietyExpenses = compShifting + compCorpus + compBetterment + compTotalRent + compTotalGST;
    
    // Total Expenses
    const fsiTotalCosts = fsiCosts?.totalCosts || 0;
    const totalDACharges = daChargesCosts?.totalDACharges || 0;
    const totalConstructedArea = totalCarpetArea + (totalCarpetArea * loadingPct / 100);
    const totalConstructedAreaSqft = totalConstructedArea / conversionFactor;
    const constructionCostPerSqft = parseFloat(formData.constructionCostPerSqft) || 0;
    const totalConstructionCost = totalConstructedAreaSqft * constructionCostPerSqft;
    const ourOtherCosts = ((parseFloat(formData.marketingCostPerSqft) || 0) + (parseFloat(formData.miscExpPerSqft) || 0)) * ourSaleableAreaSqft;
    const compOtherCosts = ((parseFloat(formData.marketingCostPerSqft) || 0) + (parseFloat(formData.miscExpPerSqft) || 0)) * compSaleableAreaSqft;
    const durationYears = (parseInt(formData.projectDuration) || 48) / 12;
    const projectInterestRate = parseFloat(formData.projectInterestRate) || 12;
    const baseCostsForInterest = fsiTotalCosts + (totalConstructionCost * 0.15);
    const ourBankInterest = baseCostsForInterest * (projectInterestRate / 100) * durationYears;
    const compBankInterest = baseCostsForInterest * (projectInterestRate / 100) * compDurationYears;
    
    const ourTotalExpenses = fsiTotalCosts + totalDACharges + ourSocietyExpenses + totalConstructionCost + ourOtherCosts + ourBankInterest;
    const compTotalExpenses = fsiTotalCosts + totalDACharges + compSocietyExpenses + totalConstructionCost + compOtherCosts + compBankInterest;
    
    // Profit
    const ourProfit = ourRevenue - ourTotalExpenses;
    const compProfit = compRevenue - compTotalExpenses;
    const ourProfitMargin = ourRevenue > 0 ? (ourProfit / ourRevenue) * 100 : 0;
    const compProfitMargin = compRevenue > 0 ? (compProfit / compRevenue) * 100 : 0;
    
    // Required sale rate for competitor to match our profit margin
    const targetMargin = ourProfitMargin / 100;
    const compRequiredSaleRate = targetMargin < 1 
      ? compTotalExpenses / (compSaleableAreaSqft * (1 - targetMargin))
      : 0;

    return {
      competitorName: firstCompetitor.name || 'Competitor 1',
      ourFreeAreaPercent,
      compFreeAreaPercent,
      compAreaSharingPercent,
      ourSocietyShare,
      ourDeveloperShare,
      compSocietyShare,
      compDeveloperShare,
      ourSaleableAreaSqft,
      compSaleableAreaSqft,
      ourRevenue,
      compRevenue,
      ourSocietyExpenses,
      compSocietyExpenses,
      ourTotalExpenses,
      compTotalExpenses,
      ourProfit,
      compProfit,
      ourProfitMargin,
      compProfitMargin,
      compRequiredSaleRate
    };
  }, [formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.projectName || !formData.societyName || !formData.location) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        project_name: formData.projectName,
        property_details: {
          society_name: formData.societyName,
          location: formData.location,
          plot_area_per_card: parseFloat(formData.plotAreaPerCard) || 0,
          road_width: parseFloat(formData.roadWidth) || 0,
          is_tod: formData.isTod === "true",
          num_members: parseInt(formData.numMembers) || 0,
          area_flats: parseFloat(formData.areaFlats) || 0,
          area_offices: parseFloat(formData.areaOffices) || 0,
          area_shops: parseFloat(formData.areaShops) || 0,
          allotted_parkings: parseInt(formData.allottedParkings) || 0,
          completion_date: formData.completionDate || null,
          rr_zone: formData.rrZone,
          village_name: formData.villageName,
          rr_rate_plot: parseFloat(formData.rrRatePlot) || 0,
          rr_rate_flat: parseFloat(formData.rrRateFlat) || 0,
          rr_rate_office: parseFloat(formData.rrRateOffice) || 0,
          rr_rate_shop: parseFloat(formData.rrRateShop) || 0,
          road_widening_area: parseFloat(formData.roadWideningArea) || 0
        },
        fsi_parameters: {
          green_fsi_category: formData.greenFSICategory,
          slum_tdr_percentage: parseFloat(formData.slumTDRPercentage) || 30,
          residential_percentage: parseFloat(formData.residentialPercentage) || 100,
          commercial_percentage: parseFloat(formData.commercialPercentage) || 0
        },
        society_financials: {
          shifting_charges: parseFloat(formData.shiftingCharges) || 0,
          corpus_charges: parseFloat(formData.corpusCharges) || 0,
          betterment_charges_type: formData.bettermentChargesType,
          betterment_charges: parseFloat(formData.bettermentCharges) || 0,
          rent_per_sqft_flat: parseFloat(formData.rentPerSqftFlat) || 0,
          rent_per_sqft_office: parseFloat(formData.rentPerSqftOffice) || 0,
          rent_per_sqft_shop: parseFloat(formData.rentPerSqftShop) || 0,
          rent_increment_pa: parseFloat(formData.rentIncrementPA) || 5,
          project_duration: parseInt(formData.projectDuration) || 0,
          additional_area: parseFloat(formData.additionalArea) || 0,
          refundable_deposit: formData.refundableDeposit,
          deposit_months: parseInt(formData.depositMonths) || 1,
          brokerage_payable: formData.brokeragePayable,
          brokerage_months: parseInt(formData.brokerageMonths) || 1,
          society_pmc_payable: formData.societyPMCPayable,
          pmc_amount: parseFloat(formData.pmcAmount) || 0
        },
        development_parameters: {
          proposed_built_up_area: parseFloat(formData.proposedBuiltUpArea) || 0,
          fsi: parseFloat(formData.fsi) || 0,
          num_units: parseInt(formData.numUnits) || 0,
          num_floors: parseInt(formData.numFloors) || 0,
          proposed_flats_area: parseFloat(formData.proposedFlatsArea) || 0,
          proposed_offices_area: parseFloat(formData.proposedOfficesArea) || 0,
          proposed_shops_area: parseFloat(formData.proposedShopsArea) || 0
        },
        financial_parameters: {
          construction_cost_per_sqft: parseFloat(formData.constructionCost) || 0,
          sale_rate_flat_per_sqft: parseFloat(formData.saleRateFlat) || 0,
          sale_rate_office_per_sqft: parseFloat(formData.saleRateOffice) || 0,
          sale_rate_shop_per_sqft: parseFloat(formData.saleRateShop) || 0,
          legal_costs: parseFloat(formData.legalCosts) || 0,
          marketing_costs: parseFloat(formData.marketingCosts) || 0,
          other_costs: parseFloat(formData.otherCosts) || 0,
          contingency_percentage: parseFloat(formData.contingency) || 0
        }
      };

      const response = await axios.post(`${API}/projects`, payload);
      setResults(response.data);
      toast.success("Analysis completed successfully!");
    } catch (error) {
      console.error("Error calculating feasibility:", error);
      toast.error("Failed to calculate feasibility. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResults(null);
  };

  if (results) {
    return <ResultsView project={results} onBack={handleReset} />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              data-testid="back-home-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2">
              <img 
                src="https://nirman-workhub.com/nirman-logo.png" 
                alt="Nirman" 
                className="h-8 w-auto"
              />
              <div className="hidden sm:block">
                <span className="font-heading font-bold text-lg tracking-tight">Feasibility Calculator</span>
                <p className="text-[10px] text-muted-foreground -mt-1">by Nirman Developers</p>
              </div>
              <span className="sm:hidden font-heading font-bold text-sm tracking-tight">Feasibility Calc</span>
              {currentProjectId && (
                <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded">Saved</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* New Project Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNewProject}
              data-testid="new-project-btn"
            >
              New
            </Button>
            
            {/* Load Project Dialog */}
            <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="load-project-btn">
                  <FolderOpen className="h-4 w-4 mr-2" />
                  Load
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Load Project</DialogTitle>
                  <DialogDescription>Select a saved project to load</DialogDescription>
                </DialogHeader>
                {user?.role === "admin" && savedProjects.length > 0 && (
                  <div className="border border-amber-200 bg-amber-50 rounded-sm px-3 py-2 flex items-center justify-between gap-3" data-testid="refresh-snapshots-row">
                    <div className="text-xs text-amber-800">
                      <p className="font-semibold">Admin: Refresh Comparison Snapshots</p>
                      <p className="text-[11px] text-amber-700/80">Re-computes <code>computed_metrics</code> for every owned project so the Comparison tab matches the live software.</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRefreshAllSnapshots}
                      disabled={refreshingAll}
                      className="rounded-sm whitespace-nowrap"
                      data-testid="refresh-all-snapshots-btn"
                    >
                      {refreshingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh All"}
                    </Button>
                  </div>
                )}
                <div className="max-h-72 overflow-y-auto space-y-2">
                  {loadingProjects ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : savedProjects.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No saved projects</p>
                  ) : (
                    savedProjects.map(project => (
                      <div
                        key={project.id}
                        className="flex items-center justify-between p-3 border rounded-sm hover:bg-secondary/50 cursor-pointer"
                        onClick={() => handleLoadProject(project.id)}
                        data-testid={`load-project-${project.id}`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{project.name}</p>
                            {project.is_shared && (
                              <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium" data-testid={`shared-badge-${project.id}`}>
                                {project.is_owner ? `Shared (${project.shared_count})` : "Shared with you"}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span>
                              Updated: {new Date(project.updated_at).toLocaleDateString()} {new Date(project.updated_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleDuplicateFromSaved(project.id, e)}
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            title="Duplicate as variant"
                            data-testid={`duplicate-project-${project.id}`}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          {project.is_owner && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleDeleteProject(project.id, e)}
                            className="h-8 w-8 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Export Project */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExportProject}
              data-testid="export-project-btn"
              title="Export project as file"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>

            {/* Import Project */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => document.getElementById("import-file-input").click()}
              data-testid="import-project-btn"
              title="Import project from file"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            <input
              id="import-file-input"
              type="file"
              accept=".json"
              className="hidden"
              onChange={handleImportProject}
            />

            {/* Share Project */}
            {currentProjectId && (
              <Dialog open={shareDialogOpen} onOpenChange={(open) => { setShareDialogOpen(open); if (open) fetchSharedUsers(currentProjectId); }}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="share-project-btn"
                    title="Share project with other users"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Share Project</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-5">
                    {/* Section 1: Collaborative Sharing */}
                    {isProjectOwner && (
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-semibold">Add Collaborator</p>
                          <p className="text-xs text-muted-foreground">All collaborators work on the same project. Changes by anyone reflect for everyone.</p>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="email"
                            placeholder="user@example.com"
                            value={shareEmail}
                            onChange={(e) => setShareEmail(e.target.value)}
                            data-testid="share-email-input"
                            onKeyDown={(e) => e.key === "Enter" && handleShareProject()}
                            className="flex-1"
                          />
                          <Button
                            size="sm"
                            onClick={handleShareProject}
                            disabled={shareLoading || !shareEmail.trim()}
                            data-testid="share-confirm-btn"
                          >
                            {shareLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add"}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* People with access */}
                    {(sharedUsers.length > 0 || projectOwner) && (
                      <div className="space-y-2 border-t pt-3">
                        <p className="text-xs font-medium text-muted-foreground">People with access</p>
                        {projectOwner && (
                          <div className="flex items-center justify-between py-1.5 px-2 bg-muted/50 rounded text-sm">
                            <div>
                              <span className="font-medium">{projectOwner.name || projectOwner.email}</span>
                              <span className="text-xs text-muted-foreground ml-2">{projectOwner.email}</span>
                            </div>
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Owner</span>
                          </div>
                        )}
                        {sharedUsers.map(u => (
                          <div key={u.user_id} className="flex items-center justify-between py-1.5 px-2 bg-muted/50 rounded text-sm" data-testid={`shared-user-${u.user_id}`}>
                            <div>
                              <span className="font-medium">{u.name || u.email}</span>
                              <span className="text-xs text-muted-foreground ml-2">{u.email}</span>
                            </div>
                            {isProjectOwner && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                onClick={() => handleRemoveSharedUser(u.email || u.user_id)}
                                data-testid={`unshare-btn-${u.user_id}`}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Section 2: Send Independent Copy */}
                    <div className="space-y-3 border-t pt-3">
                      <div>
                        <p className="text-sm font-semibold">Send Copy</p>
                        <p className="text-xs text-muted-foreground">Creates an independent version for the user. Both copies continue separately.</p>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="user@example.com"
                          value={copyEmail}
                          onChange={(e) => setCopyEmail(e.target.value)}
                          data-testid="copy-email-input"
                          onKeyDown={(e) => e.key === "Enter" && handleSendCopy(formData)}
                          className="flex-1"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendCopy(formData)}
                          disabled={copyLoading || !copyEmail.trim()}
                          data-testid="copy-confirm-btn"
                        >
                          {copyLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Copy className="h-3 w-3 mr-1" /> Send</>}
                        </Button>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            
            {/* Duplicate current project */}
            {currentProjectId && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDuplicateProject}
                data-testid="duplicate-project-btn"
                title="Duplicate as variant"
              >
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </Button>
            )}

            {/* Auto-save Status */}
            {formData.projectName && formData.projectName.trim() !== "" && (
              <div className="flex items-center text-xs text-muted-foreground">
                {isAutoSaving ? (
                  <span className="flex items-center gap-1">
                    <span className="animate-pulse">●</span> Saving...
                  </span>
                ) : lastAutoSaveTime ? (
                  <span className="flex items-center gap-1" title={`Last saved: ${lastAutoSaveTime.toLocaleTimeString()}`}>
                    <span className="text-green-500">●</span> Auto-saved
                  </span>
                ) : null}
              </div>
            )}
            
            {/* Save Project Dialog */}
            <Dialog open={saveDialogOpen} onOpenChange={(open) => {
              setSaveDialogOpen(open);
              if (open) setSaveProjectName(formData.projectName || formData.societyName || "");
            }}>
              <DialogTrigger asChild>
                <Button variant="default" size="sm" data-testid="save-project-btn">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{currentProjectId ? "Update Project" : "Save Project"}</DialogTitle>
                  <DialogDescription>
                    {currentProjectId ? "Update this project with current changes" : "Save your project for later use"}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="save-project-name">Project Name</Label>
                    <Input
                      id="save-project-name"
                      value={saveProjectName}
                      onChange={(e) => setSaveProjectName(e.target.value)}
                      placeholder={formData.projectName || "Enter project name"}
                      data-testid="save-project-name-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button onClick={handleSaveProject} data-testid="confirm-save-btn">
                    {currentProjectId ? "Update" : "Save"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* User Menu */}
            <Separator orientation="vertical" className="h-6 mx-1" />
            
            {/* Credits Display / Admin Badge */}
            {user?.role === "admin" ? (
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 rounded text-sm" data-testid="admin-badge">
                <Shield className="h-4 w-4 text-amber-600" />
                <span className="font-medium text-amber-700">Admin</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded text-sm" data-testid="credits-display">
                <Coins className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-700">{userCredits}</span>
                <span className="text-blue-600 text-xs hidden sm:inline">credits</span>
              </div>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                  {user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="h-6 w-6 rounded-full"
                    />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline max-w-[100px] truncate">{user?.name || "User"}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-sm text-muted-foreground border-b mb-1">
                  {user?.email}
                  {user?.role === "admin" && <span className="ml-1 text-[10px] bg-primary text-primary-foreground px-1 py-0.5 rounded">ADMIN</span>}
                </div>
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer" data-testid="admin-panel-btn">
                    <Shield className="h-4 w-4 mr-2" />
                    User Management
                  </DropdownMenuItem>
                )}
                {user?.role !== "admin" && (
                  <DropdownMenuItem 
                    onClick={handleRequestCredits} 
                    disabled={requestingCredits || creditRequestPending}
                    className="cursor-pointer" 
                    data-testid="request-credits-btn"
                  >
                    <Coins className="h-4 w-4 mr-2" />
                    {creditRequestPending ? "Request Pending..." : requestingCredits ? "Requesting..." : "Request Credits"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </nav>
      </header>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit}>
            {/* Project Name */}
            <Card className="rounded-sm shadow-sm mb-6" data-testid="project-name-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="font-heading tracking-tight">Project Name</CardTitle>
                  {/* Shared Project Indicator */}
                  {currentProjectId && sharedUsers.length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-sm" data-testid="shared-project-indicator">
                      <Share2 className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-700">
                        {isProjectOwner 
                          ? `Shared with ${sharedUsers.length} user${sharedUsers.length > 1 ? 's' : ''}`
                          : `Shared by ${projectOwner?.name || projectOwner?.email || 'Owner'}`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Input
                  id="projectName"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleChange}
                  placeholder="Enter project name"
                  className="rounded-sm max-w-xl"
                  data-testid="project-name-input"
                  required
                />
              </CardContent>
            </Card>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
              <TabsList className="flex flex-wrap gap-1 rounded-sm text-xs overflow-x-auto pb-2 sm:pb-0">
                <TabsTrigger value="quickinput" className="rounded-sm bg-primary/10 font-semibold text-[10px] sm:text-xs px-2 sm:px-3" data-testid="quickinput-tab">
                  Quick Input
                  {isAutoSaving && activeTab === "quickinput" && <span className="ml-1 text-[10px] text-muted-foreground hidden sm:inline">(saving...)</span>}
                </TabsTrigger>
                <TabsTrigger value="fsi" className="rounded-sm text-[10px] sm:text-xs px-2 sm:px-3" data-testid="fsi-tab">FSI Calc</TabsTrigger>
                <TabsTrigger value="costs" className="rounded-sm text-[10px] sm:text-xs px-2 sm:px-3" data-testid="costs-tab">FSI Costs</TabsTrigger>
                {formData.projectMode !== "greenfield" && (
                <TabsTrigger value="society" className="rounded-sm text-[10px] sm:text-xs px-2 sm:px-3" data-testid="society-tab">Society</TabsTrigger>
                )}
                <TabsTrigger value="dacharges" className="rounded-sm text-[10px] sm:text-xs px-2 sm:px-3" data-testid="dacharges-tab">DA Charges</TabsTrigger>
                <TabsTrigger value="othercosts" className="rounded-sm text-[10px] sm:text-xs px-2 sm:px-3" data-testid="othercosts-tab">Other Costs</TabsTrigger>
                <TabsTrigger value="revenue" className="rounded-sm text-[10px] sm:text-xs px-2 sm:px-3" data-testid="revenue-tab">Revenue</TabsTrigger>
                <TabsTrigger value="plsheet" className="rounded-sm text-[10px] sm:text-xs px-2 sm:px-3" data-testid="plsheet-tab">P&L Sheet</TabsTrigger>
                <TabsTrigger value="sensitivity" className="rounded-sm text-[10px] sm:text-xs px-2 sm:px-3" data-testid="sensitivity-tab">Sensitivity</TabsTrigger>
                <TabsTrigger value="dashboard" className="rounded-sm bg-green-100 font-semibold text-[10px] sm:text-xs px-2 sm:px-3" data-testid="dashboard-tab">Dashboard</TabsTrigger>
                <TabsTrigger value="comparison" className="rounded-sm text-[10px] sm:text-xs px-2 sm:px-3" data-testid="comparison-tab">Comparison</TabsTrigger>
                <TabsTrigger value="analysis" className="rounded-sm bg-amber-100 font-semibold text-[10px] sm:text-xs px-2 sm:px-3" data-testid="analysis-tab">Analysis</TabsTrigger>
                <TabsTrigger value="cashflow" className="rounded-sm bg-blue-100 font-semibold text-[10px] sm:text-xs px-2 sm:px-3" data-testid="cashflow-tab">Cash Flow</TabsTrigger>
              </TabsList>

              {/* Quick Input Tab */}
              <TabsContent value="quickinput" className="space-y-6">
                {currentProjectId && lastOpenedInfo && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/50 px-4 py-2 rounded-sm" data-testid="last-opened-info">
                    <Clock className="h-3.5 w-3.5" />
                    <span>
                      Last opened: {new Date(lastOpenedInfo.date).toLocaleDateString()} {new Date(lastOpenedInfo.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      {lastOpenedInfo.by && ` by ${lastOpenedInfo.by}`}
                    </span>
                  </div>
                )}
                <QuickInput 
                  formData={formData} 
                  setFormData={setFormData} 
                  onCalculate={() => handleTabChange("fsi")}
                  onQuickJump={handleQuickJump}
                  plData={plData}
                  fsiBreakdown={fsiBreakdown}
                  onValidationChange={setQuickInputHasErrors}
                  constructionCosts={constructionCosts}
                />
              </TabsContent>

              {/* FSI Calculator Tab */}
              <TabsContent value="fsi" className="space-y-6">
                <FSICalculator 
                  formData={formData} 
                  setFormData={setFormData}
                  fsiBreakdown={fsiBreakdown}
                  setFSIBreakdown={setFSIBreakdown}
                />
                {/* Navigation Buttons */}
                <div className="flex justify-between pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleTabChange("quickinput")}
                    data-testid="back-to-property-btn"
                    className="rounded-sm"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back: Quick Input
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleTabChange("costs")}
                    data-testid="next-to-costs-btn"
                    className="rounded-sm"
                  >
                    Next: FSI Costs
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              {/* FSI & Sanction Costs Tab */}
              <TabsContent value="costs" className="space-y-6">
                <FSISanctionCosts 
                  formData={formData} 
                  setFormData={setFormData}
                  fsiBreakdown={fsiBreakdown}
                  setFSICosts={setFSICosts}
                  appConfig={appConfig}
                />
                {/* Navigation Buttons */}
                <div className="flex justify-between pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleTabChange("fsi")}
                    data-testid="back-to-fsi-btn"
                    className="rounded-sm"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back: FSI Calc
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleTabChange(formData.projectMode === "greenfield" ? "dacharges" : "society")}
                    data-testid="next-to-society-btn"
                    className="rounded-sm"
                  >
                    Next: {formData.projectMode === "greenfield" ? "DA Charges" : "Society Expenses"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              {/* Society Financials Tab */}
              <TabsContent value="society" className="space-y-6">
                {formData.projectMode === "greenfield" ? (
                  <Card className="rounded-sm shadow-sm border-blue-500/30">
                    <CardHeader>
                      <CardTitle className="font-heading tracking-tight text-blue-700">
                        Greenfield / JV Project - No Society Expenses
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="bg-blue-50 p-4 rounded-sm">
                        <p className="text-sm mb-3">
                          For Greenfield/JV projects, society expenses are not applicable as there is no existing society.
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
                          <li>No shifting charges</li>
                          <li>No corpus fund</li>
                          <li>No betterment charges</li>
                          <li>No rental costs</li>
                          <li>No security deposits</li>
                          <li>No brokerage costs</li>
                        </ul>
                      </div>
                      <div className="bg-green-50 p-4 rounded-sm border border-green-200">
                        <p className="font-medium text-green-800">Landowner Share</p>
                        <p className="text-sm text-green-700 mt-1">
                          The landowner receives <strong>{formData.jvRatioPercent === "custom" ? (formData.jvRatioCustom || 30) : (formData.jvRatioPercent || 30)}%</strong> of 
                          the total saleable area as their JV share.
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Society expenses = ₹0. This will be reflected in the P&L Sheet and Comparison tab.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <SocietyFinancials 
                    formData={formData} 
                    setFormData={setFormData}
                    setSocietyCosts={setSocietyCosts}
                  />
                )}
                {/* Navigation Buttons */}
                <div className="flex justify-between pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleTabChange("costs")}
                    data-testid="back-to-costs-btn"
                    className="rounded-sm"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back: FSI Costs
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleTabChange("dacharges")}
                    data-testid="next-to-dacharges-btn"
                    className="rounded-sm"
                  >
                    Next: DA Charges
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              {/* DA Charges Tab */}
              <TabsContent value="dacharges" className="space-y-6">
                <DACharges 
                  formData={formData} 
                  setFormData={setFormData}
                  fsiBreakdown={fsiBreakdown}
                  fsiCosts={fsiCosts}
                  societyCosts={societyCosts}
                  setDAChargesCosts={setDAChargesCosts}
                />
                {/* Navigation Buttons */}
                <div className="flex justify-between pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleTabChange(formData.projectMode === "greenfield" ? "costs" : "society")}
                    data-testid="back-to-society-btn"
                    className="rounded-sm"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back: {formData.projectMode === "greenfield" ? "FSI Costs" : "Society Expenses"}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleTabChange("othercosts")}
                    data-testid="next-to-othercosts-btn"
                    className="rounded-sm"
                  >
                    Next: Other Costs
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              {/* Other Costs Tab */}
              <TabsContent value="othercosts" className="space-y-6">
                <OtherCosts 
                  formData={formData} 
                  setFormData={setFormData}
                  fsiBreakdown={fsiBreakdown}
                  setOtherCostsData={setOtherCostsData}
                />
                {/* Navigation Buttons */}
                <div className="flex justify-between pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleTabChange("dacharges")}
                    data-testid="back-to-dacharges-btn"
                    className="rounded-sm"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back: DA Charges
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleTabChange("revenue")}
                    data-testid="next-to-revenue-btn"
                    className="rounded-sm"
                  >
                    Next: Revenue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              {/* Revenue Analysis Tab */}
              <TabsContent value="revenue" className="space-y-6">
                <RevenueAnalysis 
                  formData={formData} 
                  fsiBreakdown={fsiBreakdown}
                  fsiCosts={fsiCosts}
                  daChargesCosts={daChargesCosts}
                  societyCosts={societyCosts}
                />
                {/* Navigation Buttons */}
                <div className="flex justify-between pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleTabChange("othercosts")}
                    data-testid="back-to-othercosts-btn"
                    className="rounded-sm"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back: Other Costs
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleTabChange("plsheet")}
                    data-testid="next-to-plsheet-btn"
                    className="rounded-sm"
                  >
                    Next: P&L Sheet
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              {/* P&L Sheet Tab */}
              <TabsContent value="plsheet" className="space-y-6">
                <PLSheet 
                  formData={formData}
                  fsiBreakdown={fsiBreakdown}
                  fsiCosts={fsiCosts}
                  daChargesCosts={daChargesCosts}
                  societyCosts={societyCosts}
                  otherCosts={otherCostsData}
                  competitorData={competitorData}
                />
                {/* Navigation Buttons */}
                <div className="flex justify-between pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleTabChange("revenue")}
                    data-testid="back-to-revenue-btn"
                    className="rounded-sm"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back: Revenue
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleTabChange("sensitivity")}
                    data-testid="next-to-sensitivity-btn"
                    className="rounded-sm"
                  >
                    Next: Sensitivity
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              {/* Sensitivity Analysis Tab */}
              <TabsContent value="sensitivity" className="space-y-6">
                <SensitivityAnalysis 
                  formData={formData}
                  setFormData={setFormData}
                  fsiBreakdown={fsiBreakdown}
                  fsiCosts={fsiCosts}
                  daChargesCosts={daChargesCosts}
                  societyCosts={societyCosts}
                  plData={plData}
                />
                {/* Navigation Buttons */}
                <div className="flex justify-between pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleTabChange("plsheet")}
                    data-testid="back-to-plsheet-btn"
                    className="rounded-sm"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back: P&L Sheet
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleTabChange("dashboard")}
                    data-testid="next-to-dashboard-btn"
                    className="rounded-sm"
                  >
                    Next: Dashboard
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              {/* Dashboard Tab */}
              <TabsContent value="dashboard" className="space-y-6">
                <Dashboard 
                  formData={formData}
                  fsiBreakdown={fsiBreakdown}
                  fsiCosts={fsiCosts}
                  daChargesCosts={daChargesCosts}
                  societyCosts={societyCosts}
                  plData={plData}
                />
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => handleTabChange("sensitivity")} className="rounded-sm">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back: Sensitivity
                  </Button>
                  <Button onClick={() => handleTabChange("comparison")} className="rounded-sm">
                    Next: Comparison <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              {/* Comparison Tab */}
              <TabsContent value="comparison" className="space-y-6">
                <ComparisonTab
                  formData={formData}
                  setFormData={setFormData}
                  fsiBreakdown={fsiBreakdown}
                  fsiCosts={fsiCosts}
                  daChargesCosts={daChargesCosts}
                  societyCosts={societyCosts}
                  addCompetitor={addCompetitor}
                  removeCompetitor={removeCompetitor}
                  updateCompetitor={updateCompetitor}
                  handleTabChange={handleTabChange}
                  savedProjects={savedProjects}
                />
              </TabsContent>

              {/* Analysis Tab */}
              <TabsContent value="analysis" className="space-y-6">
                <Analysis
                  formData={formData}
                  fsiBreakdown={fsiBreakdown}
                  fsiCosts={fsiCosts}
                  daChargesCosts={daChargesCosts}
                  societyCosts={societyCosts}
                  plData={plData}
                />
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => handleTabChange("comparison")} className="rounded-sm">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back: Comparison
                  </Button>
                  <Button variant="outline" onClick={() => handleTabChange("cashflow")} className="rounded-sm">
                    Next: Cash Flow <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="cashflow" className="space-y-6">
                <ExpenseCashFlow plData={plData} formData={formData} fsiCosts={fsiCosts} daChargesCosts={daChargesCosts} societyCosts={societyCosts} />
                <CashFlowAnalysis plData={plData} formData={formData} setFormData={setFormData} />
                <div className="flex justify-start">
                  <Button variant="outline" onClick={() => handleTabChange("analysis")} className="rounded-sm">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back: Analysis
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </form>
        </div>
      </section>
    </div>
  );
}